import { IconButton, Snackbar, Step, StepContent, StepLabel, Stepper, Typography } from '@mui/material';
import L from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet/dist/leaflet.css';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Accordion, Button, Container, FormControl, InputGroup, ListGroup, Offcanvas } from 'react-bootstrap';
import { MdAdd, MdClose } from 'react-icons/md';
import { MapContainer, Marker, Polyline, Popup, TileLayer } from 'react-leaflet';
import lockIcon from '../img/chevron-droit.png';
import halteIcon from '../img/HalteDePlaisance.png';
import jonctionIcon from '../img/jonction.png';
import pontIcon from '../img/pont-levis.png';
import classicPortIcon from '../img/PortDePlaisance.png';
import swapIcon from '../img/swap_vert.svg';
import tunnelIcon from '../img/Tunnel.png';
import { calculateItinerary, getPorts, searchPorts } from '../services/itineraryService';
import '../style/ItineraryPage.css';

// Function to format time in hours and minutes
const formatTime = (totalHours) => {
  const hours = Math.floor(totalHours);
  const minutes = Math.round((totalHours - hours) * 60);
  return `${hours}h ${minutes}min`;
};

// Default map center coordinates
const defaultCenter = [46.603354, 1.888334];

// Utility function to validate latitude and longitude
const isValidLatLng = (lat, lng) => {
  return !isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
};


const ItineraryPage = () => {
  // State variables
  const [ports, setPorts] = useState([]);
  const [itineraries, setItineraries] = useState([]);
  const [selectedItineraryIndex, setSelectedItineraryIndex] = useState(null);
  const [showLocks, setShowLocks] = useState(true);
  const [showPontsLevis, setShowPontsLevis] = useState(true);
  const [showHaltes, setShowHaltes] = useState(true);
  const [showJonctions, setShowJonctions] = useState(true);
  const [showClassicPorts, setShowClassicPorts] = useState(true);
  const [showPortsHorsItineraire, setShowPortsHorsItineraire] = useState(true);
  const [showUserLocation, setShowUserLocation] = useState(true);
  const [showTunnels, setShowTunnels] = useState(true);
  const [center, setCenter] = useState(defaultCenter);
  const [geolocationSupported, setGeolocationSupported] = useState(false); 
  const [steps, setSteps] = useState([
    { id: null, searchTerm: '', searchResults: [] },
    { id: null, searchTerm: '', searchResults: [] },
  ]);
  const [nearestPort, setNearestPort] = useState(null);
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const mapRef = useRef(null);
  const markerRefs = useRef([]);
  const tronconRefs = useRef([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  //Dimensions du bateau
  const [boatLength, setBoatLength] = useState('');
  const [boatWidth, setBoatWidth] = useState('');
  const [boatDraft, setBoatDraft] = useState('');
  const [boatAirDraft, setBoatAirDraft] = useState('');

 
  // Function to find the nearest port based on user's location
  const findNearestPort = useCallback((latitude, longitude) => {
    let minDistance = Infinity;
    let nearestPort = null;

    if (ports && ports.length > 0) {
      ports.forEach(port => {
        if (port.Port_Type === 1 || port.Port_Type === 2 || port.Port_Type === 4) {
          const portLat = parseFloat(port.Port_MapX);
          const portLng = parseFloat(port.Port_MapY);
          if (isValidLatLng(portLat, portLng)) {
            const distance = Math.sqrt(Math.pow(latitude - portLat, 2) + Math.pow(longitude - portLng, 2));
            if (distance < minDistance) {
              minDistance = distance;
              nearestPort = port;
            }
          }
        }
      });
    }

    setNearestPort(nearestPort);
  }, [ports]);

  // Debounced search function to avoid multiple API calls
  const debouncedSearch = useCallback(
    debounce(async (query, index) => {
      if (query && query.length > 0) {
        try {
          const data = await searchPorts(query);
          const newSteps = [...steps];
          newSteps[index].searchResults = data.ports.filter(p => !steps.map(step => step.id).includes(p.Port_Id));
          if (index === 0 && nearestPort) {
            newSteps[index].searchResults.unshift(nearestPort);
          }
          setSteps(newSteps);
        } catch (error) {
          console.error('Erreur lors de la recherche des ports:', error);
        }
      } else {
        const newSteps = [...steps];
        newSteps[index].searchResults = index === 0 && nearestPort ? [nearestPort] : [];
        setSteps(newSteps);
      }
    }, 50),
    [steps, nearestPort]
  );

  // Fetch ports on component mount
  useEffect(() => {
    const fetchPorts = async () => {
      try {
        const data = await getPorts();
        
        setPorts(data.ports);
      } catch (error) {
        console.error('Erreur lors de la récupération des ports:', error);
      }
    };

    fetchPorts();
  }, []);

  // Get user's location on component mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        position => {
          const { latitude, longitude } = position.coords;
          setCenter([latitude, longitude]);
          findNearestPort(latitude, longitude);
          setGeolocationSupported(true);
        },
        error => {
          console.error('Erreur lors de la récupération de la position:', error);
          setGeolocationSupported(false);
        }
      );
    } else {
      console.error('La géolocalisation n\'est pas supportée par ce navigateur.');
      setGeolocationSupported(false);
    }
  }, [ports, findNearestPort]);

  // Handle changes in the search input
  const handleSearchChange = (e, index) => {
    const newSteps = [...steps];
    newSteps[index].searchTerm = e.target.value;
    setSteps(newSteps);
    debouncedSearch(e.target.value, index);
  };

  // Select a port from the search results
  const handleSelectPort = (port, index) => {
    const newSteps = [...steps];
    newSteps[index] = { id: port.Port_Id, searchTerm: port.Port_Nom, searchResults: [] };
    setSteps(newSteps);
  };

  // Check if a port is part of the selected itinerary
  const isItineraryPort = (port) => {
    return selectedItinerary && getItineraryPorts(selectedItinerary).some(itineraryPort => itineraryPort?.Port_Id === port.Port_Id);
  };

  // Get all ports involved in the selected itinerary
  const getItineraryPorts = (selectedItinerary) => {
    if (!selectedItinerary || !selectedItinerary.details || !selectedItinerary.details.tronconsDetails) {
      return [];
    }
    
    return Object.values(selectedItinerary.details.tronconsDetails).flatMap((troncon) =>[
        ports.find((port) => port.Port_Id === troncon.Troncon_Port1Id),
        ports.find((port) => port.Port_Id === troncon.Troncon_Port2Id)
      ])
  };

  // Calculate the itinerary based on the selected steps
  const handleCalculateItinerary = async () => {
    const points = steps.map(step => step.id).filter(id => id !== null);
    if (points.length < 2) {
      alert('Veuillez sélectionner au moins un port de départ et un port d\'arrivée');
      return;
    }

    try {
      const data = await calculateItinerary(points, boatLength, boatWidth, boatDraft, boatAirDraft);
      if (!data || data.length === 0 || data.error) {
        handleSnackbarOpen('Aucun itinéraire trouvé');
        return;
      }
      setItineraries(data);
      setSelectedItineraryIndex(0);
      handleSnackbarOpen('Itinéraire calculé avec succès');

      // Adjust map bounds to fit the selected itinerary
      const bounds = new L.LatLngBounds();
      const selectedItinerary = data[0]; // Assuming the first itinerary is the one to fit

      if (selectedItinerary && selectedItinerary.details && selectedItinerary.details.tronconsDetails) {
        selectedItinerary.details.tronconsDetails.forEach((troncon) => {
          troncon.path.forEach((point) => {
            if (isValidLatLng(point.lat, point.lng)) {
              bounds.extend([point.lat, point.lng]);
            }
          });
        });
      }
      

      if (mapRef.current && bounds.isValid()) {
        mapRef.current.fitBounds(bounds);
      }
    } catch (error) {
      console.error('Erreur lors du calcul de l\'itinéraire:', error);
    }
  };

  // Add a new step in the itinerary
  const handleAddStep = () => {
    return new Promise((resolve) => {
      const newSteps = [...steps];
      newSteps.splice(steps.length - 1, 0, { id: null, searchTerm: '', searchResults: [] });
      setSteps(newSteps);
      handleSnackbarOpen('Étape ajoutée avec succès');

      resolve(newSteps);
    });
  };

  // Remove a step from the itinerary
  const handleRemoveStep = (index) => {
    const newSteps = [...steps];
    newSteps.splice(index, 1);
    setSteps(newSteps);
  };

  // Move a step up in the itinerary
  const handleStepUp = (index) => {
    if (index <= 0) return;
    const newSteps = [...steps];
    [newSteps[index - 1], newSteps[index]] = [newSteps[index], newSteps[index - 1]];
    setSteps(newSteps);
  };

  // Move a step down in the itinerary
  const handleStepDown = (index) => {
    if (index >= steps.length - 1) return;
    const newSteps = [...steps];
    [newSteps[index], newSteps[index + 1]] = [newSteps[index + 1], newSteps[index]];
    setSteps(newSteps);
  };

  // Swap the start and end points of the itinerary
  const handleSwapPorts = () => {
    const newSteps = [...steps];
    [newSteps[0], newSteps[newSteps.length - 1]] = [newSteps[newSteps.length - 1], newSteps[0]];
    setSteps(newSteps);
  };

  // Handle marker click on the map
  const handleMarkerClick = (port) => {
    const marker = markerRefs.current[port.Port_Id];
    if (marker) {
      marker.openPopup();
    }

  };
  // Fonction pour ouvrir les popups des tronçons
  const handleTronconClick = (troncon) => {
    const polylineRef = tronconRefs.current[troncon.tronconId];
    if (polylineRef) {
      polylineRef.openPopup();
    }
  };
  // Set a port from the map as a step in the itinerary
  const handleSetPortFromMap = (port, index) => {
    const newSteps = [...steps];
    newSteps[index] = { id: port.Port_Id, searchTerm: port.Port_Nom, searchResults: [] };
    setSteps(newSteps);
  };

  // Add a step and set a port from the map
  const handleAddStepAndSetPort = (port) => {
    const newSteps = [...steps];
    newSteps.splice(steps.length - 1, 0, { id: null, searchTerm: '', searchResults: [] });
    newSteps[newSteps.length-2] = { id: port.Port_Id, searchTerm: port.Port_Nom, searchResults: [] };
    setSteps(newSteps);
  };

  // Handle itinerary selection
  const handleItinerarySelection = (index) => {
    setSelectedItineraryIndex(index);

    // Adjust map bounds to fit the selected itinerary
    const bounds = new L.LatLngBounds();
    const selectedItinerary = itineraries[index];

    if (selectedItinerary && selectedItinerary.details && selectedItinerary.details.canals) {
      selectedItinerary.details.tronconsDetails.forEach((troncon) => {
          troncon.path.forEach((point) => {
            bounds.extend([point.lat, point.lng]);
          });
        });
    }
    if (mapRef.current) {
      mapRef.current.fitBounds(bounds);
    }
  };

  // User location icon
  const userLocationIcon = new L.Icon({
    iconUrl: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png',
    iconSize: [30, 30],
  });

  // Icons for different port types
  const iconUrls = {
    1: classicPortIcon,
    2: halteIcon,
    3: jonctionIcon,
    4: halteIcon,
    5: halteIcon,
    6: tunnelIcon,
    default: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
  };

  // Generate a unique key for each port marker
  const createKey = (port) => `${port.Port_Id}-${port.Port_MapX}-${port.Port_MapY}`;

  // Get paths for all troncons in the selected itinerary
  const getItineraryTronconsPaths = (selectedItinerary) => {
    if (!selectedItinerary || !selectedItinerary.details || !selectedItinerary.details.tronconsDetails) {
      return [];
    }


    return Object.values(selectedItinerary.details.tronconsDetails).flatMap((troncon, index) =>{
      let port1 = ports.find((port) => port.Port_Id === troncon.Troncon_Port1Id);
      let port2 = ports.find((port) => port.Port_Id === troncon.Troncon_Port2Id);
      
      if (!port1 ) {
        console.error('Port 1 introuvable pour le tronçon:', troncon.Troncon_Port1Id);
        port1 = { Port_Nom: 'Inconnu' };
      }

      if (!port2) {
        console.error('Port 2 introuvable pour le tronçon:', troncon.Troncon_Port2Id);
        port2 = { Port_Nom: 'Inconnu' };
      }
      return (
        <Polyline
          key={index}
          positions={troncon.path.map((point) => [point.lat, point.lng])}
          color={"#0000FF"}
          opacity={0.8}
          weight={8}
          eventHandlers={{
            click: () => handleTronconClick(troncon) }}
          ref={(ref) => { tronconRefs.current[troncon.tronconId] = ref; }}
          >
        <Popup 
        position={troncon.path[parseInt(troncon.path.length/2)]} // Position initiale du tronçon
      >
        <div className="popup-container"> 
          <h4><strong>{port2.Port_Nom} ➔ {port1.Port_Nom}</strong></h4>
          <p>Distance 📏: {parseInt(10*troncon.troncon_Km)/10} km</p>
          <p>Temps estimé ⏱️: {formatTime(troncon.tronconTemps)}</p>
          <p>Nombre d'écluses 🚧: {troncon.Troncon_Ecluses}</p>
          <p>Vitesse maximale 🏎️: {troncon.Troncon_Vmax} km/h</p>
          {troncon.Troncon_Tunnel && parseFloat(troncon.Troncon_Tunnel) > 0 && (
            <p>Tunnel 🚇: {parseFloat(troncon.Troncon_Tunnel)} km</p>
          )}
          <br />
          {troncon.Troncon_NbPontLevis > 0 && (
            <p>Ponts-levis 🌉: {troncon.Troncon_NbPontLevis}</p>
          )}
          <div> <h5>Dimensions max du bateau:</h5></div>
            <div>Longueur: <span>{troncon.Longueur} m</span></div>
            <div>Largeur: <span>{troncon.Largeur} m</span></div>
            <div>Tirant d'eau: <span>{troncon.TirantEau} m</span></div>
            <div>Tirant d'air: <span>{troncon.TirantAir} m</span></div>
        </div>
      </Popup>
      </Polyline>
      );
    });
  };

  // Get lock markers for the selected itinerary
  const getItineraryLocks = (selectedItinerary) => {
    if (!selectedItinerary || !selectedItinerary.details || !selectedItinerary.details.canals) {
      return [];
    }

    return Object.values(selectedItinerary.details.tronconsDetails).flatMap((troncon) => troncon.tronconLocks.map((lock, index) => ({
      position: [parseFloat(lock.Ecluse_MapX), parseFloat(lock.Ecluse_MapY)],
      title: lock.Ecluse_Nom,
      key: `${index}`
    })));
  };

  // Get pont-levis markers for the selected itinerary
  const getItineraryPontsLevis = (selectedItinerary) => {
    if (!selectedItinerary || !selectedItinerary.details || !selectedItinerary.details.canals) {
      return [];
    }

    return Object.values(selectedItinerary.details.tronconsDetails).flatMap((troncon) => troncon.tronconPontsLevis.map((pontLevis, index) => ({
      position: [parseFloat(pontLevis.Pontlevis_MapX), parseFloat(pontLevis.Pontlevis_MapY)],
      title: pontLevis.Pontlevis_Nom,
      key: `${index}`
    })));
  };

  // Get all canals in the selected itinerary
  const getItineraryCanals = (selectedItinerary) => {
    if (!selectedItinerary || !selectedItinerary.details || !selectedItinerary.details.canals) {
      return [];
    }

    return Object.values(selectedItinerary.details.canals);
  };

  // Get summaries for all itineraries
  const getItinerarySummaries = () => {
    return itineraries.map((itinerary, index) => {
      const name = itinerary.name;
      const details = itinerary.details;

      return (
        <ListGroup.Item
          key={index}
          className={`list-group-item-itinerary ${selectedItineraryIndex === index ? 'selected' : ''}`}
          onClick={() => handleItinerarySelection(index)}
        >
          <strong>{name} : </strong>
          <span>{parseInt(details.total_Km)} km, {details.total_Ecluses} écl, {formatTime(details.total_Temps.toFixed(2))}</span>
        </ListGroup.Item>
      );
    });
  };

  // Zoom to the bounds of a specific canal
  const zoomToCanal = (canal) => {
    const bounds = new L.LatLngBounds();
    const CLE = canal.CLE;
    if (selectedItinerary && selectedItinerary.details && selectedItinerary.details.tronconsDetails) {
      selectedItinerary.details.tronconsDetails.forEach((troncon) => {
        if (troncon.canalCLE === CLE) {
          troncon.path.forEach((point) => {
            bounds.extend([point.lat, point.lng]);
          });
        }
      });
    if (mapRef.current && bounds.isValid()) {
      mapRef.current.fitBounds(bounds);
    }
  };
  }
  // Zoom to a specific port
  const zoomToPort = (port) => {
    if (mapRef.current && port) {
      const latLng = [parseFloat(port.Port_MapX), parseFloat(port.Port_MapY)];
      if (isValidLatLng(latLng[0], latLng[1])) {
        mapRef.current.setView(latLng, 14); // Adjust the zoom level as needed
        handleMarkerClick(port);
      }
    }
  };

  const zoomToTroncon = (troncon) => {
    if (mapRef.current && troncon) {
      
      const bounds = new L.LatLngBounds(troncon.path.map(point => [point.lat, point.lng]));
      if (bounds.isValid()) {
        mapRef.current.fitBounds(bounds);
      }
      handleTronconClick(troncon);
    }
  }
  const cleanHTML = (htmlString) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    let textContent = doc.body.textContent || '';

    // Remove excessive whitespace
    textContent = textContent.replace(/\s+/g, ' ').trim();

    // Normalize line breaks
    textContent = textContent.replace(/<br\s*\/?>/gi, '\n');

    // Decode HTML entities
    const textarea = document.createElement('textarea');
    textarea.innerHTML = textContent;
    textContent = textarea.value;

    return textContent;
  };

  const decodeHtmlEntity = (str) => {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = str;
    return textarea.value;
  };
  
  // Fonction pour nettoyer et normaliser les données HTML
  const cleanText = (htmlString) => {
    let textContent = decodeHtmlEntity(htmlString);
  
    // Supprimer les espaces blancs excessifs
    textContent = textContent.replace(/\s+/g, ' ').trim();
  
    // Normaliser les sauts de ligne
    textContent = textContent.replace(/<br\s*\/?>/gi, '\n');
  
    return textContent;
  };

  
  // Render details of a specific troncon
  const renderTronconDetails = (troncon, startPortId ) => {
    let primaryPk ;
    let primaryPort ;
    if (startPortId == troncon.Troncon_Port1Id) {
      primaryPk = troncon.port1_pk;
      primaryPort = ports.find((port) => port.Port_Id === troncon.Troncon_Port1Id);
    }
    else {
      primaryPk = troncon.port2_pk;
      primaryPort = ports.find((port) => port.Port_Id === troncon.Troncon_Port2Id);
    }
    
    return (
      <div >
        <StepLabel>
          <div onClick={(e) => {
            e.stopPropagation();
            zoomToPort(primaryPort);
          }}>
          <Typography variant="subtitle1">
            <strong>Pk {primaryPk} {primaryPort ? primaryPort.Port_Nom : 'Inconnu'}</strong>
          </Typography>
          </div>
        </StepLabel>
        <StepContent>
          <div onClick={(e) => {
        e.stopPropagation();
        zoomToTroncon(troncon);
      }}>
          <Typography>
            📏 {parseInt(troncon.troncon_Km * 10) / 10} km - ⏱️ {formatTime(troncon.tronconTemps)}
          </Typography>
          <Typography>
            🚧 {troncon.Troncon_Ecluses} écl - 🏎️ {troncon.Troncon_Vmax} km/h
          </Typography>
          {troncon.Troncon_Tunnel && parseFloat(troncon.Troncon_Tunnel) > 0 && (
            <Typography>🚇 Tunnel : {parseFloat(troncon.Troncon_Tunnel)} km </Typography>
          )}
          {troncon.Troncon_NbPontLevis > 0 && (
            <Typography>🌉 {troncon.Troncon_NbPontLevis} ponts-levis</Typography>
          )}
          {primaryPort && primaryPort.Port_Embranchement_Info && (
            <Typography>🔗 Jonctions: {cleanHTML(primaryPort.Port_Embranchement_Info)}</Typography>
          )}
          </div>
        </StepContent>
        
      </div>
    );
  };
  
  const renderDetails = (selectedItinerary) => {
    let i=0;
    let canalIndex=0;
    const path = selectedItinerary.path;
    let startPortId = path[0];

    let canal = selectedItinerary.details.canals[0];
    let canalTroncons = [];
    
    return (
      <Accordion defaultActiveKey="0">
        {selectedItinerary.details.tronconsDetails.map((tronconsDetails, tronconIndex) => {
          
          const currentcanal= selectedItinerary.details.canals.find(c => c.CLE === tronconsDetails.canalCLE);
          startPortId = path[i];
          i++;

          if (currentcanal.CLE !== canal.CLE ) {
    
            const canalTronconsTemp = canalTroncons;
            const startPort = ports.find(p => p.Port_Id == startPortId);
            let last = ' (Jonction)';
            
            canalTronconsTemp.push(
              <Step key={tronconIndex+1} active>
               <div onClick={(e) => {
                e.stopPropagation();
                zoomToPort(startPort);
              }}>
                  <StepLabel>
                    <Typography variant="subtitle1">
                      <strong>{last} à {startPort.Port_Nom}</strong>
                    </Typography>
                  </StepLabel>
                  </div>
              </Step>
                )
            canalTroncons = [];
            canalTroncons.push(
              <Step key={tronconIndex} active>

                {renderTronconDetails(tronconsDetails, startPortId)}
              </Step>
            );
            const canalTemp = canal;
            canal = currentcanal;
            canalIndex++;
            return (
              <Accordion.Item
                eventKey={`${canalIndex}`}
                key={canalIndex}
                onClick={() => zoomToCanal(canalTemp)}
              >
                <Accordion.Header>
                  <Typography variant="h7">
                    <strong>{cleanText(canalTemp.LIBELLE)} :</strong>
                    <br />
                    Temps : {formatTime(canalTemp.tempsCanal)}
                    <br />
                    Distance : {cleanText(canalTemp.distanceCanal)} km
                    <br />
                    Ecluses : {cleanText(canalTemp.ecluses)}
                  </Typography>
                </Accordion.Header>
                <Accordion.Body>
                  <Stepper orientation="vertical">
                    {canalTronconsTemp}
                  </Stepper>
                </Accordion.Body>
              </Accordion.Item>
            );
          }
          else {
            canalTroncons.push(                    
              <Step key={tronconIndex} active>
                
                {renderTronconDetails(tronconsDetails, startPortId)}
              </Step>);
          }
        })}
         {(() => {
        // Ajout d'une étape de jonction ou d'arrivée pour le dernier canal
        let last = '';
        if (i === path.length - 1) {
          last = ' (Arrivée)';
        } else {
          last = ' (Jonction)';
        }
        const lastTronconIndex = selectedItinerary.details.tronconsDetails.length - 1;
        const lastEndPortId = path[path.length - 1];
        const lastEndPort = ports.find(p => p.Port_Id === lastEndPortId);
        canalTroncons.push(
          <Step key={lastTronconIndex + 1} active>
            <div onClick={(e) => {
              e.stopPropagation();
              zoomToPort(lastEndPort);
            }}>
              <StepLabel>
                <Typography variant="subtitle1">
                  <strong>{last} à {lastEndPort.Port_Nom}</strong>
                </Typography>
              </StepLabel>
            </div>
          </Step>
        );

        return (
          <Accordion.Item
            eventKey={`${canalIndex + 1}`}
            key={canalIndex + 1}
            onClick={() => zoomToCanal(canal)}
          >
            <Accordion.Header>
              <Typography variant="h7">
                <strong>{cleanText(canal.LIBELLE)} :</strong>
                <br />
                Temps : {formatTime(canal.tempsCanal)}
                <br />
                Distance : {cleanText(canal.distanceCanal)} km
                <br />
                Ecluses : {cleanText(canal.ecluses)}
              </Typography>
            </Accordion.Header>
            <Accordion.Body>
              <Stepper orientation="vertical">
                {canalTroncons}
              </Stepper>
            </Accordion.Body>
          </Accordion.Item>
        );
      })()}
    </Accordion>
  );
};
  
  
  
  

  // Legend box for the map
  // Légende pour la carte
const LegendBox = () => (
  <div className="legend-box">
    <h4>🗺️ Map</h4>
    <div>
      <input type="checkbox" id="showPortsHorsItineraire" checked={showPortsHorsItineraire} onChange={() => setShowPortsHorsItineraire(!showPortsHorsItineraire)} />
      <label htmlFor="showPortsHorsItineraire">
        <img src="https://maps.google.com/mapfiles/ms/icons/red-dot.png" alt="Port" style={{ width: '20px', verticalAlign: 'middle' }} /> Ports
      </label>
    </div>
    <div>
      <input type="checkbox" id="showUserLocation" checked={showUserLocation} onChange={() => setShowUserLocation(!showUserLocation)} />
      <label htmlFor="showUserLocation">
        <img src="https://maps.google.com/mapfiles/ms/icons/blue-dot.png" alt="Localisation" style={{ width: '20px', verticalAlign: 'middle' }} /> Localisation
      </label>
    </div>
  </div>
);


  // Itinerary box for the map
  const ItineraryBox = () => (
    <div className="itinerary-box">
      <h4>📍 Itinéraire</h4>
      <div>
        <input type="checkbox" id="showClassicPorts" checked={showClassicPorts} onChange={() => setShowClassicPorts(!showClassicPorts)} />
        <label htmlFor="showClassicPorts">
          <img src={classicPortIcon} alt="Port classique" style={{ width: '20px', verticalAlign: 'middle' }} /> Ports de plaisance
        </label>
      </div>
      <div>
        <input type="checkbox" id="showHaltes" checked={showHaltes} onChange={() => setShowHaltes(!showHaltes)} />
        <label htmlFor="showHaltes">
          <img src={halteIcon} alt="Halte" style={{ width: '20px', verticalAlign: 'middle' }} /> Ports haltes
        </label>
      </div>
      <div>
        <input type="checkbox" id="showJonctions" checked={showJonctions} onChange={() => setShowJonctions(!showJonctions)} />
        <label htmlFor="showJonctions">
          <img src={jonctionIcon} alt="Jonction" style={{ width: '20px', verticalAlign: 'middle' }} /> Jonctions
        </label>
      </div>
      <div>
        <input type="checkbox" id="showTunnels" checked={showTunnels} onChange={() => setShowTunnels(!showTunnels)} />
        <label htmlFor="showTunnels">
          <img src={tunnelIcon} alt="Tunnel" style={{ width: '20px', verticalAlign: 'middle' }} /> Tunnels
        </label>
      </div>
      <div>
        <input type="checkbox" id="showLocks" checked={showLocks} onChange={() => setShowLocks(!showLocks)} />
        <label htmlFor="showLocks">
          <img src={lockIcon} alt="Écluse" style={{ width: '20px', verticalAlign: 'middle' }} /> Écluses
        </label>
      </div>
      <div>
        <input type="checkbox" id="showPontsLevis" checked={showPontsLevis} onChange={() => setShowPontsLevis(!showPontsLevis)} />
        <label htmlFor="showPontsLevis">
          <img src={pontIcon} alt="Pont-levis" style={{ width: '20px', verticalAlign: 'middle' }} /> Ponts-levis
        </label>
      </div>
    </div>
  );

  // Snackbar handlers
  const handleSnackbarOpen = (message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  // Get the selected itinerary details
  const selectedItinerary = selectedItineraryIndex !== null ? itineraries[selectedItineraryIndex] : null;

  return (
    <Container fluid className="app-container">
      <MapContainer
        center={center}
        zoom={8}
        scrollWheelZoom={true}
        style={{ height: '100%', width: '100%' }}
        ref={mapRef}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />

        <LegendBox />
        <ItineraryBox />

        {geolocationSupported && showUserLocation && (
          <Marker
            position={center}
            title="Vous êtes ici"
            icon={userLocationIcon}
          />
        )}

        {ports
          .filter(port => isItineraryPort(port))
          .filter(port =>
            (showClassicPorts && port.Port_Type === 1) ||
            (showHaltes && port.Port_Type === 2) ||
            (showJonctions && port.Port_Type === 3) ||
            (showHaltes && port.Port_Type === 4) ||
            (showHaltes && port.Port_Type === 5) ||
            (showTunnels && port.Port_Type === 6) ||
            (showPortsHorsItineraire && !isItineraryPort(port))
          )
          .map((port) => {
            const lat = parseFloat(port.Port_MapX);
            const lng = parseFloat(port.Port_MapY);
            if (isValidLatLng(lat, lng)) {
              return (
                <Marker
                  key={createKey(port)}
                  position={[lat, lng]}
                  title={port.Port_Nom}
                  icon={L.icon({
                    iconUrl: iconUrls[port.Port_Type] || iconUrls.default,
                    iconSize: [25, 25],
                  })}
                  // Add a click event handler to the marker
                  eventHandlers={{
                    click: () => zoomToPort(port)
                  }}
                  ref={(ref) => { markerRefs.current[port.Port_Id] = ref; }}
                >
                  <Popup
                    position={[lat, lng]}
                  >
                    <div className="popup-container">
                      <h2>{port.Port_Nom}</h2>
                      <p><strong> {port.Port_Type === 1 ? 'Port de plaisance' : port.Port_Type === 2 ? 'Halte' : port.Port_Type === 3 ? 'Jonction' : port.Port_Type === 4 ? 'Escale' : port.Port_Type === 5 ? 'Bief' : port.Port_Type === 6 ? 'Tunnel' : 'Jonction'}</strong></p>
                      <p></p>
                      <Button onClick={() => handleSetPortFromMap(port, 0)}>
                        Définir comme départ
                      </Button>
                      <Button onClick={() => {
                        handleAddStepAndSetPort(port);
                      }}>
                        Ajouter comme étape
                      </Button>
                      <Button onClick={() => handleSetPortFromMap(port, steps.length - 1)}>
                        Définir comme arrivée
                      </Button>
                    </div>
                  </Popup>
                </Marker>
              );
            }
            return null;
          })
        }

        {showPortsHorsItineraire && ports
          .filter(port => [1, 2, 4].includes(port.Port_Type) && !isItineraryPort(port))
          .map((port) => {
            const lat = parseFloat(port.Port_MapX);
            const lng = parseFloat(port.Port_MapY);
            if (isValidLatLng(lat, lng)) {
              return (
                <Marker
                  key={createKey(port)}
                  position={[lat, lng]}
                  title={port.Port_Nom}

                  icon={L.icon({
                    iconUrl: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
                    iconSize: [40, 40],
                  })}
                  eventHandlers={{
                    click: () => zoomToPort(port)
                  }}
                  ref={(ref) => { markerRefs.current[port.Port_Id] = ref; }}
                >
                  <Popup
                    position={[lat, lng]}
                  >
                    <div className="popup-container">
                      <h2>{port.Port_Nom}</h2>
                      <p><strong> {port.Port_Type === 1 ? 'Port de plaisance' : port.Port_Type === 2 ? 'Halte' : port.Port_Type === 3 ? 'Jonction' : port.Port_Type === 4 ? 'Escale' : port.Port_Type === 5 ? 'Bief' : port.Port_Type === 6 ? 'Tunnel' : 'Jonction'}</strong></p>
                      <Button onClick={() => handleSetPortFromMap(port, 0)}>
                        Définir comme départ
                      </Button>
                      <Button onClick={() => {
                        handleAddStepAndSetPort(port);
                      }}>
                        Ajouter comme étape
                      </Button>
                      <Button onClick={() => handleSetPortFromMap(port, steps.length - 1)}>
                        Définir comme arrivée
                      </Button>
                    </div>
                  </Popup>
                </Marker>
              );
            }
            return null;
          })}

        {selectedItinerary && getItineraryTronconsPaths(selectedItinerary)}

    
        {showLocks && selectedItinerary && getItineraryLocks(selectedItinerary).map((lock, index) => (
          <Marker
            key={index}
            position={lock.position}
            title={lock.title}
            icon={L.icon({
              iconUrl: lockIcon, // Replace with the actual path to the lock icon
              iconSize: [25, 25],
            })}
          >
            <Popup>
              <div className="popup-container">
                <h2>{lock.title}</h2>
              </div>
            </Popup>
          </Marker>
        ))}

        {showPontsLevis && selectedItinerary && getItineraryPontsLevis(selectedItinerary).map((pontLevis, index) => (
          <Marker
            key={index}
            position={pontLevis.position}
            title={pontLevis.title}
            icon={L.icon({
              iconUrl: pontIcon, // Replace with the actual path to the pont-levis icon
              iconSize: [25, 25],
            })}
          >
            <Popup className="popup-container">
              <div>
                <h2>{pontLevis.title}</h2>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      <Button
        variant="primary"
        className="sidebar-toggle"
        onClick={() => setShowOffcanvas(true)}
      >
        ☰
      </Button>

      <Offcanvas
        show={showOffcanvas}
        onHide={() => setShowOffcanvas(false)}
        backdrop={false}
        className="custom-offcanvas"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>FluviaCap</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <div className="search-container">
            {steps.map((step, index) => (
              <InputGroup className="mb-3" key={index}>
                <FormControl
                  placeholder={`Choisissez un ${index === 0 ? 'point de départ' : index === steps.length - 1 ? 'point d\'arrivée' : 'étape'}`}
                  aria-label={`Choisissez un ${index === 0 ? 'point de départ' : index === steps.length - 1 ? 'point d\'arrivée' : 'étape'}`}
                  value={step.searchTerm}
                  onChange={(e) => handleSearchChange(e, index)}
                  onClick={() => {
                    if (index === 0 && nearestPort) {
                      const newSteps = [...steps];
                      newSteps[index].searchResults.unshift(nearestPort);
                      setSteps(newSteps);
                    }
                  }}
                />
                <InputGroup.Text>{index === 0 ? '🚩' : index === steps.length - 1 ? '📍' : '➡️'}</InputGroup.Text>
                {index > 0 && index < steps.length - 1 && (
                  <>
                    <Button variant="danger" onClick={() => handleRemoveStep(index)} className="remove-step-button">
                      X
                    </Button>
                    <Button variant="secondary" onClick={() => handleStepUp(index)} className="step-up-button">
                      ↑
                    </Button>
                    <Button variant="secondary" onClick={() => handleStepDown(index)} className="step-down-button">
                      ↓
                    </Button>
                  </>
                )}
              </InputGroup>
            ))}
            <Button variant="secondary" onClick={handleSwapPorts} className="swap-button">
              <img src={swapIcon} alt="Swap" className="swap-icon" />
            </Button>
            <Button variant="secondary" onClick={handleAddStep} className="add-step-button">
              <MdAdd /> Ajouter une étape
            </Button>
          </div>
          {steps.map((step, stepIndex) => (
            <ListGroup className="mt-4" key={stepIndex}>
              {step.searchResults.map((port, index) => (
                <ListGroup.Item key={index} onClick={() => handleSelectPort(port, stepIndex)}>
                  {port.Port_Id === nearestPort?.Port_Id ? 'Port le plus proche' : port.Port_Nom}
                </ListGroup.Item>
              ))}
            </ListGroup>
          ))}
           <h4>Dimensions du bateau (facultatif)</h4>
          <div className="dimensions-form">
            <div className="dimension-input">
              <div className="dimension-label">Longueur (m) :</div>
              <InputGroup>
                <FormControl
                  value={boatLength}
                  onChange={(e) => setBoatLength(e.target.value)}
                />
              </InputGroup>
            </div>
            <div className="dimension-input">
              <div className="dimension-label">Largeur (m) :</div>
              <InputGroup>
                <FormControl
                  value={boatWidth}
                  onChange={(e) => setBoatWidth(e.target.value)}
                />
              </InputGroup>
            </div>
            <div className="dimension-input">
              <div className="dimension-label">Tirant d'air (m) :</div>
              <InputGroup>
                <FormControl
                  value={boatAirDraft}
                  onChange={(e) => setBoatAirDraft(e.target.value)}
                />
              </InputGroup>
            </div>
            <div className="dimension-input">
              <div className="dimension-label">Tirant d'eau (m) :</div>
              <InputGroup>
                <FormControl
                  value={boatDraft}
                  onChange={(e) => setBoatDraft(e.target.value)}
                />
              </InputGroup>
            </div>
          </div>
            
          <Button className="mt-1" onClick={handleCalculateItinerary}>Calculer l'itinéraire</Button>

          {itineraries.length > 0 && (
            <div className="mt-3">
              <h4>Itinéraires possible</h4>
              <ListGroup>{getItinerarySummaries()}</ListGroup>
            </div>
          )}
          {selectedItinerary && (
            <div className="mt-4">
              <h4>Détails de l'itinéraire</h4>
              {renderDetails(selectedItinerary)}
            </div>
          )}
        </Offcanvas.Body>
      </Offcanvas>

      {!showOffcanvas && (
        <div className="itinerary-info" onClick={() => setShowOffcanvas(true)}>
          <span>{steps[0]?.searchTerm || 'Départ'} ➔ {steps[steps.length - 1]?.searchTerm || 'Arrivée'}</span>
        </div>
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        action={
          <IconButton size="small" aria-label="close" color="inherit" onClick={handleSnackbarClose}>
            <MdClose fontSize="small" />
          </IconButton>
        }
      />
    </Container>
  );
};

export default ItineraryPage;
